import type { RequestStatus } from '@features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { BimcCalculationLimitedCostData } from 'gql/graphql';

import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { bimcCalculationLimitedCostsGql } from '../bimCalculationLimitedQueries';

interface IUseBimCalculationLimitedCosts {
    calculationId?: number;
    currentPage?: number;
    currentPageSize?: number;
    constructionTypeId: number;
}

const requestId = 'bimcCalculationLimitedCosts';

export const useBimCalculationLimitedCosts = (
    props: IUseBimCalculationLimitedCosts,
): {
    data: BimcCalculationLimitedCostData[] | null | undefined;
    changeFilterHandler: ({ fieldName, checkedValues }: { fieldName: any; checkedValues: any }) => void;
    doReload: () => void;
    calculationLimitedCostsStatus: RequestStatus;
} => {
    const dispatch = useDispatch();
    const { calculationId, currentPageSize, currentPage, constructionTypeId } = props;

    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFilterValues((prevValue) => ({
            ...prevValue,
            [fieldName]: checkedValues,
        }));
    }, []);

    const variables = useMemo(
        () => ({
            calculationId: calculationId as number,
            constructionTypeId,
            skip: currentPageSize ? (currentPage || 0) * currentPageSize : undefined,
            take: currentPageSize,
        }),
        [constructionTypeId, calculationId, currentPage, currentPageSize],
    );

    const [calculationLimitedCostsData, calculationLimitedCostsStatus, , calculationLimitedCostsMakeRequest] =
        useRequest({
            requestId,
            query: bimcCalculationLimitedCostsGql,
            withEffect: Boolean(calculationId) && Boolean(constructionTypeId),
            variables,
        });

    const doReload = useCallback(() => {
        dispatch(calculationLimitedCostsMakeRequest(variables));
    }, [calculationLimitedCostsMakeRequest, dispatch, variables]);

    const data = useMemo(() => {
        const items = calculationLimitedCostsData?.bimcCalculationLimitedCosts?.items as
            | BimcCalculationLimitedCostData[]
            | null
            | undefined;

        const activeFilters = Object.entries(filterValues).filter(([, values]) => values?.length);

        if (!items || !activeFilters.length) {
            return items;
        }

        return items.filter((item) =>
            activeFilters.every(([key, values]) =>
                values.includes(String(item[key as keyof BimcCalculationLimitedCostData] ?? '')),
            ),
        );
    }, [calculationLimitedCostsData?.bimcCalculationLimitedCosts?.items, filterValues]);

    return {
        data,
        calculationLimitedCostsStatus,
        doReload,
        changeFilterHandler,
    };
};
